import { useMemo } from 'react';

import { useIsFocused } from '@sg/garnish';

import { useIsLoggedIn } from '../../context/AuthMachine';
import {
  type MenuReorderRailOrdersQuery,
  useMenuReorderRailOrdersQuery,
} from './graphql/MenuReorderRailOrders.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useMenuReorderRailOrders = (params: Params) => {
  const { restaurantId, enabled } = params;

  const isFocused = useIsFocused();
  const isLoggedIn = useIsLoggedIn();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [{ data, fetching }] = useMenuReorderRailOrdersQuery({
    variables: { input: { take: ORDERS_TO_FETCH, page: 1 }, restaurantId },
    pause: !enabled || !isLoggedIn || !isFocused,
    requestPolicy: 'cache-and-network',
  });

  // ─── Derived Data ────────────────────────────────────────────────────

  const orders = useMemo<Orders>(() => {
    const ordersData = data?.ordersV2;

    if (ordersData?.__typename !== 'OrdersResponseSuccess') return EMPTY_ORDERS;

    return ordersData.orders;
  }, [data]);

  const mostRecentLineItems = useMemo(() => {
    return orders
      .flatMap((order) => order.lineItems.map((lineItem) => ({ ...lineItem, order })))
      .filter((lineItem) => lineItem.canReorderAtRestaurant)
      .slice(0, MAX_ITEMS);
  }, [orders]);

  const mostRecentOrder = orders[0];

  // ─────────────────────────────────────────────────────────────────────

  return {
    isFetchingMostRecentOrders: fetching,
    mostRecentOrder,
    mostRecentLineItems,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ORDERS_TO_FETCH = 10;
const MAX_ITEMS = 6;
const EMPTY_ORDERS: Orders = [];

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  restaurantId: number;
  enabled: boolean;
};

type Orders = NonNullable<
  Extract<MenuReorderRailOrdersQuery['ordersV2'], { __typename: 'OrdersResponseSuccess' }>['orders']
>;
